import { useState } from 'react'

import styled from 'styled-components'

import {
  type IssueTokenResponse,
  issueStreamAccess,
} from 'services/endpoints/streaming'
import { useViewerStore } from 'store/viewer'

type Props = {
  username: string
  onAuthorized: (resp: IssueTokenResponse) => void
}

export default function PasscodeGate({ username, onAuthorized }: Props) {
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const submit = async () => {
    if (!code.trim() || loading) return

    setErr(null)
    setLoading(true)

    try {
      console.log('[PasscodeGate] requesting token for', username)

      const res = await issueStreamAccess({ username, pass_code: code })

      console.log('[PasscodeGate] authorized, got URL:', res.playback_url)

      // Do not set viewer count here anymore.
      // Presence channel membership is the source of truth for live viewer count.

      if (res.goal?.public) {
        useViewerStore.getState().setCurrentStreamGoal({
          stream_id: res.goal.stream_id,
          user_id: res.goal.user_id,
          goal_id: res.goal.goal_id,
          amount_goal:
            res.goal.amount_goal ?? null,
          amount_tipped: res.goal.amount_tipped ?? 0,
          remaining:
            res.goal.remaining ?? null,
          percent:
            res.goal.percent != null ? Number(res.goal.percent) : null,
          reward: res.goal.reward ?? null,
          public: Boolean(res.goal.public),
          completed: Boolean(res.goal.completed),
        })
      } else {
        useViewerStore.getState().clearCurrentStreamGoal()
      }

      onAuthorized(res)
    } catch (e: any) {
      const msg =
        e?.response?.data?.error || e?.message || 'Authorization failed'
      console.error('[PasscodeGate] token request failed:', msg)
      setErr(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Backdrop>
      <Card
        onSubmit={(e) => {
          e.preventDefault()
          if (!loading) void submit()
        }}
      >
        <Title>Enter Pass Code</Title>
        <Input
          type="password"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Pass code"
          autoFocus
        />
        {err && <Error>{err}</Error>}
        <Row>
          <Btn type="button" onClick={() => setCode('')} disabled={loading}>
            Clear
          </Btn>
          <Btn type="submit" disabled={loading || code.length === 0}>
            {loading ? 'Checking…' : 'Enter'}
          </Btn>
        </Row>
      </Card>
    </Backdrop>
  )
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const Card = styled.form`
  width: 90%;
  max-width: 420px;
  background: #111;
  color: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.6);
`

const Title = styled.h3`
  margin: 0 0 16px;
`

const Input = styled.input`
  width: 100%;
  height: 44px;
  border-radius: 10px;
  border: 1px solid #333;
  background: #1a1a1a;
  color: #fff;
  padding: 0 12px;
  outline: none;
`

const Error = styled.div`
  color: #ff7a7a;
  margin-top: 8px;
`

const Row = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  justify-content: flex-end;
`

const Btn = styled.button`
  height: 40px;
  padding: 0 16px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  background: #2e97fe;
  color: #fff;
  font-weight: 600;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`